import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import { buildVendorParam } from "../utils/vendorSlug";
import CrawlLink from "../components/CrawlLink";
import VendorDirectoryLinks from "../components/VendorDirectoryLinks";
import {
  flattenVendorLocations,
  locationCountLabel,
  onePerVendor,
  withDistance,
} from "../utils/vendorOutlets";
import { SEOHead } from "../components/SEO/SEOHead";
import styled, { keyframes } from "styled-components";
import useCatalogUpdates from "../hooks/useCatalogUpdates";
import { useDispatch, useSelector } from "react-redux";
import {
  applyRestaurantHoursChange,
  fetchRestaurants,
} from "../redux/restaurantSlice";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShareIcon from "@mui/icons-material/Share";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
// Fork, plate and knife on a plain near-white field: the one restaurant
// placeholder, shared with the storefront tiles and both apps.
import placeholderRestaurantImage from "../assets/img/restaurant-dish-placeholder.png";
import DeliveryBadge from "../components/DeliveryBadge";
import VendorShareModal from "../components/Share/VendorShareModal";
import LocationPrompt from "../components/LocationPrompt";
import {
  calculateDistance,
  checkDeliveryAvailability,
} from "../redux/userLocationSlice";
import useCoverageBatch from "../redux/useCoverageBatch";
import useDeliveryPoint from "../hooks/useDeliveryPoint";
import { getOptimizedUrl } from "../utils/cloudinaryOptimize";
import {
  shareCardImageFor,
  withVendorCacheBuster,
} from "../components/SEO/vendorSeo";
import {
  COUNTRY_NAME,
  PRIMARY_CITY,
  SITE_ORIGIN,
  formatCurrency,
} from "../config/market";
import { VendorOfferTag } from "../components/DealTag";
import { openNowOf, readVendorOpenState } from "../utils/vendorOpenState";
import { ORDER_AHEAD_LABEL } from "../utils/deliverySchedule";
import VendorTagline from "../components/VendorTagline";
import {
  coverDishOf,
  dishesOf,
  restaurantHeroOf,
  tidyCuisine,
  tidyName,
} from "../utils/restaurantListing";

// The id the ORDER pipeline uses for this location. Outlets resolve their
// PARENT vendor (same as the checkout gate does today; see the outlet-coverage
// gap note in Lusuku_Playbooks/12): coverage shown here therefore always
// matches what checkout will decide.
const vendorIdFor = (location) =>
  location.isOutlet
    ? location.owner || location.restaurantId
    : location.owner || location._id;

// The restaurant a card stands for, whichever of its locations it opens on:
// one card per restaurant since 4 Oct 2026, titled with the restaurant.
const restaurantNameOf = (location) =>
  location.isOutlet
    ? location.parentRestaurantName || location.parentVendorName || location.name
    : location.name;

/**
 * What this card can offer a hungry person, read straight off the listing
 * payload. GET /restaurants already embeds `sections[].products[]` with an
 * image and a price on every dish, plus `isOpen` and `ownerDetails`, and the
 * card was showing none of it: a logo, a street address and a chore. You
 * cannot make anyone hungry with a street address.
 *
 * No new request and no backend change. Every restaurant in production today
 * has an image on 100% of its dishes, so the strip is never ragged.
 */
const restaurantSignals = (location) => {
  const dishes = dishesOf(location);

  const prices = dishes
    .map((product) => Number(product.price))
    .filter((price) => Number.isFinite(price) && price > 0);

  // "Fast food,cakes,pastries " is a real value in production, so it needs
  // trimming and a separator before it can sit in a chip.
  const cuisine = String(location.ownerDetails?.typeOfGoods || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" · ");

  // ONE reading of this row's open state, for both the pill's wording and the
  // dimmed-photo treatment. Taking `isOpen` off the raw row while reading
  // `orderAhead` through the helper let a card and the storefront it opens
  // disagree: the helper prefers the server-computed `openStatus`, the raw
  // field can be a stale legacy value. `readVendorOpenState` already falls
  // back to that same field when a row carries no `openStatus`.
  const openState = readVendorOpenState(location);

  return {
    // The card leads with ONE dish at full width rather than three crops of
    // one. Three 90x62 slivers of a square crop read as a contact sheet; a
    // single plate at the card's full width reads as dinner.
    // A card image the vendor uploaded for the LIST (Restaurant profile,
    // "Card image", exact 1440x900) wins over the pinned dish; the alt text
    // then names the restaurant rather than a dish it is not showing.
    hero: restaurantHeroOf(location, dishes),
    dishes: dishes.slice(0, 3),
    dishCount: dishes.length,
    fromPrice: prices.length ? Math.min(...prices) : null,
    cuisine,
    // true, false, or null for "nothing published" (the pill then draws
    // nothing rather than claiming the kitchen is trading).
    isOpen: openState.isOpen,
    // Closed now but taking orders for later (the row's server-computed
    // openStatus). The card says "Order ahead" instead of "Closed" and keeps
    // its full-colour photograph.
    orderAhead: openState.scheduledOrdersOnly,
    // The store-wide offer, already judged live by the server, or null.
    offerSummary: location.offerSummary || null,
  };
};

/**
 * The one place that knows what a restaurant's menu URL looks like.
 *
 * The card's onClick and the card's share button MUST agree: a share that
 * lands somewhere other than where the tap lands is worse than no share at
 * all, and the two drifted apart the moment they were written twice.
 */
const buildRestaurantPath = (location) => {
  const param = buildVendorParam(location.name, vendorIdFor(location));
  return location.isOutlet
    ? `/restaurant/shop/${param}?outletId=${location._id}`
    : `/restaurant/shop/${param}`;
};

/**
 * The line of copy under a shared link.
 *
 * Deliberately assembled the same way `restaurantSeoFromOgCard` assembles it
 * in netlify/edge-functions/og-inject.js — cuisine, then dishes and the price
 * floor, then where the kitchen is — because this string is shown in the share
 * sheet as a PREVIEW of the card the recipient will get. A preview that reads
 * differently from the real thing is worse than no preview.
 *
 * Everything in it is already on the card; nothing here costs a request.
 */
const shareDescriptionOf = (location, signals) => {
  const bits = [];
  if (signals.cuisine) bits.push(signals.cuisine);
  if (signals.dishCount > 0 && signals.fromPrice) {
    bits.push(
      `${signals.dishCount} ${signals.dishCount === 1 ? "dish" : "dishes"} from ${formatCurrency(signals.fromPrice)}`,
    );
  } else if (signals.dishCount > 0) {
    bits.push(
      `${signals.dishCount} ${signals.dishCount === 1 ? "dish" : "dishes"}`,
    );
  }
  if (location.address) bits.push(location.address);
  return bits.join(" · ").slice(0, 160);
};

/* The hero's atmosphere used to come from a scatter of cutlery icons at low
   opacity, then from photographs. It now comes from the information itself
   (see Board): what is cooking, what it leads with, what it costs. Drawn forks
   at four percent opacity were decoration pretending to be texture, and the
   photographs were the grid's job done twice.

   Everything the hero shows is real and none of it is new work for the API.
   `GET /restaurants` already returns every dish with its image and price, plus
   each kitchen's live open state. The page was throwing all of it away above
   the fold and showing a sentence instead. */

/* Whether the board is open, remembered per browser.
 *
 * A collapsed board is a real preference rather than a transient bit of state:
 * somebody who scrolls straight to the grid every visit should not have to
 * close it every visit. It defaults to OPEN, because the board is the reason
 * the hero exists and a first-time visitor should see it working.
 *
 * Web storage throws outright in some privacy modes, so every access is
 * wrapped, matching the convention already used for pendingBrandQuote. */
const BOARD_PREF_KEY = "lusuku:restaurants:boardOpen";

const readBoardPref = () => {
  try {
    return localStorage.getItem(BOARD_PREF_KEY) !== "0";
  } catch (_) {
    /* storage disabled; the default stands */
    return true;
  }
};

const writeBoardPref = (open) => {
  try {
    localStorage.setItem(BOARD_PREF_KEY, open ? "1" : "0");
  } catch (_) {
    /* storage disabled; the choice simply does not persist */
  }
};

const heroSignals = (locations) => {
  // Outlets inherit their parent's menu, so counting them would double every
  // number and repeat every plate.
  const restaurants = (locations || []).filter((l) => !l.isOutlet);

  /* THE BOARD.
   *
   * There is no photograph in this hero, and that is a decision rather than a
   * shortage. The grid immediately below already shows a photograph for every
   * restaurant on the page, so a picture up here was always going to be the
   * same food twice, competing with itself. Three versions proved it: a
   * drifting wall of twenty tiles, then one large plate bleeding off the right
   * edge, then this. The wall spent the most valuable space on the platform's
   * weakest images; the single plate looked like every other split hero on the
   * internet and cropped the dish past recognition.
   *
   * What is left is the one thing this page knows that nobody can copy: which
   * kitchens are cooking RIGHT NOW, what each of them leads with, and what it
   * costs. Every row is a fact about this minute. That is what earns the space,
   * and it is also what makes somebody hungry, because "Passion Juice, UGX
   * 8,000, open now" is a decision they can act on and a photograph of a juice
   * is not.
   *
   * Closed kitchens are named at the foot rather than listed. They are still on
   * the page, in the grid, but the board is about what is available, and a row
   * you cannot act on is worse than no row.
   */
  const board = restaurants
    .filter((r) => r.isOpen)
    .map((r) => {
      const menu = dishesOf(r);
      // The same dish the CARD leads with, so the board and the grid never
      // disagree, and so pinning a cover dish moves both at once.
      const lead = coverDishOf(r, menu);
      const prices = menu
        .map((dish) => Number(dish.price))
        .filter((price) => Number.isFinite(price) && price > 0);
      return {
        restaurant: r,
        lead,
        dishCount: menu.length,
        from: prices.length ? Math.min(...prices) : null,
        cuisine: tidyCuisine(r.ownerDetails?.typeOfGoods),
      };
    })
    .filter((row) => row.lead);

  const closedNames = restaurants
    .filter((r) => !r.isOpen)
    .map((r) => r.name.trim());

  // The floor quoted in the hero comes from OPEN kitchens only. Advertising a
  // price nobody can pay tonight is a promise the page cannot keep.
  const openFloors = board.map((row) => row.from).filter(Boolean);

  return {
    board,
    closedNames,
    fromOpen: openFloors.length ? Math.min(...openFloors) : null,
    openCount: restaurants.filter((r) => r.isOpen).length,
    total: restaurants.length,
    dishCount: restaurants.reduce((sum, r) => sum + dishesOf(r).length, 0),
  };
};

/* "Restaurants" is a category label, and category labels do not make anyone
   hungry. A question does, and naming the actual meal makes it specific
   enough to answer. */
const mealTimeWord = () => {
  const hour = new Date().getHours();
  if (hour < 11) return "this morning";
  if (hour < 16) return "today";
  return "tonight";
};

const AllRestaurantsPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { restaurantData, isFetching } = useSelector(
    (state) => state.restaurants,
  );
  const userLocation = useSelector((state) => state.userLocation);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDelivery, setFilterDelivery] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  /* The restaurant whose share sheet is open, or null. Holds the whole
     payload rather than an id so the sheet can render its preview without
     re-deriving signals the card already computed. */
  const [shareTarget, setShareTarget] = useState(null);
  const searchInputRef = useRef(null);

  /* TWO pieces of state, because they answer different questions.
   *
   * `boardOpen` is a PREFERENCE: how this person likes the page when they are
   * browsing, remembered across visits.
   *
   * `boardWhileSearching` is about this search and nothing else. Typing folds
   * the board away so the results have the screen, but somebody who then
   * reopens it mid-search has not changed their mind about how the page should
   * look tomorrow, so that choice is deliberately NOT persisted and is thrown
   * away the moment the field is cleared.
   *
   * Collapsing by writing to `boardOpen` would have been one state variable and
   * would have quietly rewritten the preference of everyone who ever typed. */
  const [boardOpen, setBoardOpen] = useState(readBoardPref);
  const [boardWhileSearching, setBoardWhileSearching] = useState(null);

  const handleImageLoaded = useCallback((locationId) => {
    setLoadedImages((prev) => ({ ...prev, [locationId]: true }));
  }, []);

  // Publish / unpublish reaches customers immediately.
  //
  // This page had no socket at all, so a kitchen taken off the platform stayed
  // on screen until the visitor reloaded. `catalog:public` is the room the
  // server broadcasts visibility on, and the one the storefront page below
  // already joins; the older restaurantActivationChanged event aims at rooms
  // nothing joins, which is why deactivation never propagated either.
  useCatalogUpdates({
    "restaurant:visibilityChanged": (payload) => {
      if (!payload?.restaurantId) return;
      // Refetch rather than splice the row out: the server owns the rule for
      // what a customer may see, market scoping included.
      dispatch(fetchRestaurants());
    },
    // Opening hours are a DELTA, patched in place. Deliberately not a refetch:
    // a vendor editing their schedule must not make every connected customer
    // re-request the whole catalogue, since that load grows with the number of
    // customers rather than with the number of edits. Only a change to WHICH
    // kitchens may be shown (the event above) is worth a refetch.
    restaurantHoursUpdated: (payload) => {
      if (!payload?.restaurantId) return;
      dispatch(applyRestaurantHoursChange(payload));
    },
  });

  useEffect(() => {
    dispatch(fetchRestaurants());
  }, [dispatch]);

  const handleRestaurantClick = (location) => {
    history.push(buildRestaurantPath(location));
  };

  /**
   * Share one restaurant.
   *
   * This used to call `navigator.share` and hand the entire moment to the
   * operating system: an unbranded sheet on mobile, and on desktop Chrome —
   * which has no `navigator.share` at all — a silent clipboard write. Sharing
   * a restaurant is the most valuable thing a happy customer does here, and it
   * was the least designed screen in the product.
   *
   * Now it opens our own sheet, which shows the card the recipient will get
   * before anything is sent. The native sheet is still in there as "More
   * apps", one option among several rather than the whole feature.
   *
   * `withVendorCacheBuster` is not decoration: a scraper caches the FIRST card
   * it ever fetches for a URL for up to a month, so without a rotating suffix
   * a restaurant shared once before it had a logo would keep serving the
   * generic Lusuku card long after uploading one.
   */
  const handleShare = useCallback((event, location, signals) => {
    event.stopPropagation();
    event.preventDefault();

    setShareTarget({
      id: location._id,
      name: location.name,
      /* The sheet's whole promise is "what people will see", so its image
         is resolved by the one client-side mirror of the crawler's order
         (shareCardImageFor), with the crop hint the preview needs. The hero
         is already the card image or the cover dish, whichever the listing
         card leads with. */
      ...(({ image, fit }) => ({ imageUrl: image, imageFit: fit }))(
        shareCardImageFor(location, signals?.hero?.image)
      ),
      description: shareDescriptionOf(location, signals),
      url: withVendorCacheBuster(
        `${SITE_ORIGIN}${buildRestaurantPath(location)}`,
        location,
      ),
    });
  }, []);

  // Restaurants and their outlets as one flat list of locations
  // (utils/vendorOutlets, shared with the pharmacy and brand listings),
  // grouped below into one card per restaurant.
  const allLocations = useMemo(
    () =>
      flattenVendorLocations(restaurantData, {
        // Only VERIFIED kitchens are listed, and their outlets with them.
        isListed: (restaurant) =>
          restaurant.ownerDetails?.sellerVerification === "VERIFIED",
        outletRow: (restaurant, outlet) => ({
          restaurantId: outlet.restaurantId || restaurant._id,
          parentRestaurantId: restaurant._id,
          parentRestaurantName: restaurant.name,
          // An outlet serves its parent's menu and keeps its parent's
          // hours, so it inherits the signals the card renders. Without
          // these an outlet card would be the old bare logo-and-address
          // while every other card carried dishes.
          sections: restaurant.sections,
          // The card a restaurant chose for the list (its uploaded card
          // image, or its pinned cover dish) is the RESTAURANT's, so it stays
          // on the card whichever branch the card opens on (4 Oct 2026).
          cardImageUrl: restaurant.cardImageUrl,
          // One card per restaurant: a branch row speaks with its parent's
          // tagline, as it does with its parent's name and photo.
          tagline: restaurant.tagline,
          listingHero: restaurant.listingHero,
          // An outlet that inherits its parent's hours has no `isOpen` of
          // its own and follows the parent. One with CUSTOM hours does,
          // and the server keeps it current; reading the parent's value
          // unconditionally showed such an outlet as open while it was
          // closed, and made the hours delta above invisible for outlets.
          isOpen: outlet.isOpen ?? restaurant.isOpen,
          totalProductCount: restaurant.totalProductCount,
        }),
      }),
    [restaurantData],
  );

  const hero = useMemo(() => heroSignals(allLocations), [allLocations]);

  // Each location's straight-line distance from the shopper's browse point,
  // for choosing which branch a card opens on and for the "1.2 km" on it.
  // Unchanged when no location is set.
  const deliveryPoint = useDeliveryPoint();
  const locatedLocations = useMemo(
    () =>
      allLocations.map((location) =>
        withDistance(location, deliveryPoint, calculateDistance),
      ),
    [allLocations, deliveryPoint],
  );

  /* ONE lowercased haystack per location, built once.
   *
   * Search used to look at the restaurant name and its street address only,
   * which meant somebody who knows what they want to EAT could not find it:
   * "pilau" returned nothing unless a kitchen happened to be called Pilau
   * Connection. The menu is already in this payload, so every dish name, the
   * cuisine and the parent restaurant's name all belong in the same scan.
   *
   * Precomputed rather than walked per keystroke. With seven kitchens either
   * would do; with several hundred, rebuilding a few thousand lowercased
   * strings on every character typed is exactly the kind of thing that makes
   * a search field feel broken on a mid-range phone.
   *
   * Dishes are included here even when they have NO photo. A dish without a
   * picture is still something you can order, and the card's hero is a
   * separate question from whether the kitchen should be findable. */
  const searchIndex = useMemo(() => {
    const index = new Map();
    allLocations.forEach((location) => {
      const dishes = (location.sections || [])
        .flatMap((section) => section.products || [])
        .filter((product) => product && product.enabled !== false);

      index.set(String(location._id), {
        dishes,
        haystack: [
          location.name,
          location.address,
          location.parentRestaurantName,
          location.ownerDetails?.typeOfGoods,
          ...dishes.map((dish) => dish.name),
        ]
          .filter(Boolean)
          .join(" \n ")
          .toLowerCase(),
      });
    });
    return index;
  }, [allLocations]);

  const query = searchQuery.trim().toLowerCase();

  // ── AUTHORITATIVE coverage for every listed vendor (one batched call) ────
  // Same backend road-distance + radius verdict the order-create gate uses, so
  // a card can never say "Delivers" and then 422 at checkout. The old client
  // haversine (straight-line, default radius 5 vs the backend's 10) stays only
  // as a sorting fallback while the batch is in flight.
  const vendorIds = useMemo(
    () => allLocations.map(vendorIdFor).filter(Boolean),
    [allLocations],
  );
  const { byVendorId: coverageByVendor } = useCoverageBatch({
    vendorIds,
    enabled: vendorIds.length > 0,
  });

  // One place that answers "does this location deliver / how far?": the
  // backend verdict when we have it, a client-side estimate meanwhile.
  const deliveryInfoFor = useCallback(
    (location) => {
      const entry = coverageByVendor[String(vendorIdFor(location))];
      if (entry?.status === "succeeded" && entry.deliverable !== null) {
        return {
          available: entry.deliverable,
          distance: Number.isFinite(Number(entry.distanceKm))
            ? Number(entry.distanceKm)
            : null,
          authoritative: true,
          entry,
        };
      }
      const est = checkDeliveryAvailability(location, userLocation);
      return {
        available: est.available,
        distance: est.distance,
        authoritative: false,
        entry,
      };
    },
    [coverageByVendor, userLocation],
  );

  // Filter locations on the search query and delivery availability, then
  // show ONE card per restaurant.
  const shownRestaurants = useMemo(() => {
    let filtered = query
      ? locatedLocations.filter((location) =>
          searchIndex.get(String(location._id))?.haystack.includes(query),
        )
      : locatedLocations;

    // If filter by delivery is enabled and user has location
    if (filterDelivery && userLocation.latitude && userLocation.longitude) {
      filtered = filtered.filter(
        (location) => deliveryInfoFor(location).available === true,
      );
    }

    // ONE CARD PER RESTAURANT (4 Oct 2026). A kitchen with branches used to
    // be a card per branch, each the same menu and the same photograph. Each
    // restaurant now shows once, opened on the branch that serves this
    // shopper best (utils/vendorOutlets.onePerVendor: open, within its own
    // delivery radius, nearest first), with the number of locations on the
    // card; the storefront's location picker lists them all. Reach is the
    // straight-line estimate per branch, because the coverage batch above is
    // asked about the PARENT for every branch and so cannot tell two
    // branches apart. A search that matches one branch ("Ntinda") opens the
    // card on that branch, and the count is still the whole estate.
    let cards = onePerVendor(filtered, {
      allCards: locatedLocations,
      isOpen: openNowOf,
    });

    // Sort by delivery availability if user has location
    if (userLocation.latitude && userLocation.longitude) {
      cards = cards.sort((a, b) => {
        const aDelivery = deliveryInfoFor(a);
        const bDelivery = deliveryInfoFor(b);

        // Delivering locations first
        if (aDelivery.available && !bDelivery.available) return -1;
        if (!aDelivery.available && bDelivery.available) return 1;

        // Then sort by distance
        return (aDelivery.distance ?? 999) - (bDelivery.distance ?? 999);
      });
    }

    return cards;
  }, [
    locatedLocations,
    query,
    searchIndex,
    filterDelivery,
    userLocation,
    deliveryInfoFor,
  ]);

  /* Which of a restaurant's dishes the query actually hit, so a card that
     turned up because of its MENU can say so. Without this, searching "pilau"
     surfaces kitchens whose names contain no such word and the result looks
     arbitrary rather than clever. */
  const matchedDishesFor = useCallback(
    (location) => {
      if (!query) return [];
      return (searchIndex.get(String(location._id))?.dishes || [])
        .filter((dish) =>
          String(dish.name || "")
            .toLowerCase()
            .includes(query),
        )
        .slice(0, 3);
    },
    [query, searchIndex],
  );

  const boardExpanded = query ? boardWhileSearching === true : boardOpen;

  const toggleBoard = useCallback(() => {
    if (query) {
      setBoardWhileSearching((open) => !open);
      return;
    }
    setBoardOpen((open) => {
      writeBoardPref(!open);
      return !open;
    });
  }, [query]);

  // Clearing the field hands the board back to the saved preference.
  useEffect(() => {
    if (!query) setBoardWhileSearching(null);
  }, [query]);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    // Back to the field rather than to the top of the document, so the next
    // thing typed lands where the person is already looking.
    searchInputRef.current?.focus();
  }, []);

  // Restaurants, not locations: a kitchen with branches is one card.
  const restaurantCount = shownRestaurants.length;

  return (
    <PageWrapper>
      <SEOHead
        title="All Restaurants"
        description={`Browse all restaurants on Lusuku. Order food online with fast delivery across ${COUNTRY_NAME}.`}
        canonicalPath="/restaurants"
      />
      <Navbar />
      <PageContainer>
        <HeaderSection>
          <HeaderContent>
            <HeaderTop>
              <div>
                <Eyebrow>Restaurants · {PRIMARY_CITY}</Eyebrow>
                <Title>
                  What are you eating <em>{mealTimeWord()}?</em>
                </Title>
              </div>
              <SearchBar>
                <SearchIcon style={{ fontSize: 20, color: "#9aa7ac" }} />
                <SearchInput
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search restaurants or dishes…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    // Escape clears rather than blurs, which is what every
                    // other search field a person uses all day already does.
                    if (e.key === "Escape" && searchQuery) {
                      e.preventDefault();
                      clearSearch();
                    }
                  }}
                  aria-label="Search restaurants and dishes"
                />
                {searchQuery && (
                  <ClearSearch
                    type="button"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <CloseIcon fontSize="inherit" />
                  </ClearSearch>
                )}
              </SearchBar>
            </HeaderTop>

            <FilterRow>
              <LocationPrompt compact />
              {userLocation.latitude && userLocation.longitude && (
                <DeliveryFilterButton
                  $active={filterDelivery}
                  onClick={() => setFilterDelivery(!filterDelivery)}
                >
                  {filterDelivery ? "Show all" : "Delivers to me"}
                </DeliveryFilterButton>
              )}
            </FilterRow>

            {hero.board.length > 0 && (
              <Board>
                <BoardHead
                  type="button"
                  onClick={toggleBoard}
                  aria-expanded={boardExpanded}
                  aria-controls="cooking-right-now"
                >
                  <BoardLabel>
                    Cooking right now
                    {/* The count lives in the LABEL rather than the body, so
                        the one line that survives collapsing still says how
                        many kitchens are cooking. */}
                    <BoardCount>{hero.board.length}</BoardCount>
                    <BoardChevron $open={boardExpanded} aria-hidden="true">
                      <ExpandMoreIcon fontSize="inherit" />
                    </BoardChevron>
                  </BoardLabel>
                  <BoardMeta>
                    {hero.fromOpen != null && (
                      <>from {formatCurrency(hero.fromOpen)} · </>
                    )}
                    {hero.dishCount} dishes across {hero.total}{" "}
                    {hero.total === 1 ? "kitchen" : "kitchens"}
                  </BoardMeta>
                </BoardHead>

                <BoardPanel id="cooking-right-now" hidden={!boardExpanded}>
                  {hero.board.map((row) => (
                    <BoardRow
                      key={`board-${row.restaurant._id}`}
                      type="button"
                      onClick={() =>
                        history.push(buildRestaurantPath(row.restaurant))
                      }
                      title={`Open ${row.restaurant.name}`}
                    >
                      <BoardKitchen>
                        <b>
                          <OpenDot aria-hidden="true" />
                          {row.restaurant.name}
                        </b>
                        {row.cuisine && <span>{row.cuisine}</span>}
                      </BoardKitchen>
                      <BoardLead>
                        <b>{tidyName(row.lead.name)}</b>
                        <span>
                          {Number(row.lead.price) > 0 && (
                            <em>{formatCurrency(Number(row.lead.price))}</em>
                          )}
                          {row.dishCount > 0 && (
                            <>
                              {Number(row.lead.price) > 0 ? " · " : ""}
                              {row.dishCount}{" "}
                              {row.dishCount === 1 ? "dish" : "dishes"}
                            </>
                          )}
                        </span>
                      </BoardLead>
                    </BoardRow>
                  ))}

                  {/* Named, not listed. A closed kitchen is still on this page,
                    in the grid below; it just has no business occupying a row
                    on a board about what is available now. */}
                  {hero.closedNames.length > 0 && (
                    <BoardFoot>
                      <b>{hero.closedNames.join(", ")}</b> open later today
                    </BoardFoot>
                  )}
                </BoardPanel>
              </Board>
            )}
          </HeaderContent>
        </HeaderSection>

        <ContentSection>
          {isFetching ? (
            <SkeletonContainer>
              <SkeletonResultsCount />
              <RestaurantsGrid>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <SkeletonCard key={i}>
                    <SkeletonLogoArea />
                    <SkeletonCardBody>
                      <SkeletonLine width="70%" height="17px" />
                      <SkeletonAddressRow>
                        <SkeletonCircle size="16px" />
                        <SkeletonLine width="85%" height="13px" />
                      </SkeletonAddressRow>
                      <SkeletonLine
                        width="45%"
                        height="22px"
                        style={{ marginTop: "4px", borderRadius: "11px" }}
                      />
                    </SkeletonCardBody>
                  </SkeletonCard>
                ))}
              </RestaurantsGrid>
            </SkeletonContainer>
          ) : shownRestaurants.length === 0 ? (
            <EmptyState>
              <EmptyStateIconWrap>
                {searchQuery ? (
                  <SearchOffIcon style={{ fontSize: 34, color: "#9aa7ac" }} />
                ) : (
                  <StorefrontIcon style={{ fontSize: 34, color: "#9aa7ac" }} />
                )}
              </EmptyStateIconWrap>
              <EmptyStateTitle>No restaurants found</EmptyStateTitle>
              <EmptyStateText>
                {searchQuery
                  ? "Try a different name or location."
                  : filterDelivery
                    ? "None of our partner restaurants deliver to your location yet."
                    : "No restaurants available at the moment."}
              </EmptyStateText>
              {(searchQuery || filterDelivery) && (
                <EmptyStateAction
                  onClick={() => {
                    setSearchQuery("");
                    setFilterDelivery(false);
                  }}
                >
                  Clear search & filters
                </EmptyStateAction>
              )}
            </EmptyState>
          ) : (
            <>
              <ResultsCount>
                {restaurantCount} restaurant{restaurantCount !== 1 ? "s" : ""}
              </ResultsCount>
              <RestaurantsGrid>
                {shownRestaurants.map((location) => {
                  const signals = restaurantSignals(location);
                  const matched = matchedDishesFor(location);
                  const name = restaurantNameOf(location);
                  // "3 locations" on a chain's card; null for a kitchen with
                  // one place to order from.
                  const chainLabel = locationCountLabel(location.locationCount);
                  // Image state follows the RESTAURANT, not the branch: the
                  // card can switch branch (coverage arriving, a search)
                  // while its picture stays the same and never loads again.
                  const imageKey = location.vendorKey;
                  return (
                    <RestaurantCard
                      key={`restaurant-${location.vendorKey}`}
                      onClick={() => handleRestaurantClick(location)}
                    >
                      {/* The photo carries the name, so one link covers both.
                          It is a real href so search engines reach the menu. */}
                      <CrawlLink
                        to={buildRestaurantPath(location)}
                        onNavigate={() => handleRestaurantClick(location)}
                        style={{ display: "block" }}
                      >
                        <MediaFrame>
                          {!loadedImages[imageKey] && <LogoSkeleton />}
                          {/* The hero is a dish when the kitchen has one, and the
                            restaurant's own logo on a warm plate when it does
                            not, so a card never arrives empty. */}
                          <MediaImage
                            data-card-media
                            src={
                              signals.hero
                                ? getOptimizedUrl(signals.hero.image, "cardHero")
                                : location.imageUrl
                                  ? getOptimizedUrl(
                                      location.imageUrl,
                                      "vendorLogoCard",
                                    )
                                  : placeholderRestaurantImage
                            }
                            alt={
                              signals.hero && !signals.hero.custom
                                ? `${signals.hero.name} at ${name}`
                                : name
                            }
                            /* A LOGO must never be cropped, so it is contained
                               on its plate. The PLACEHOLDER is not a logo:
                               containing it with 14% padding left a small mark
                               adrift in a large frame. It fills the card like a
                               photograph would, which is the only way the mark is
                               big enough to read at this size. */
                            style={
                              signals.hero || !location.imageUrl
                                ? undefined
                                : { objectFit: "contain", padding: "14%" }
                            }
                            $isLoaded={loadedImages[imageKey]}
                            $closed={signals.isOpen === false && !signals.orderAhead}
                            loading="lazy"
                            onLoad={() => handleImageLoaded(imageKey)}
                            onError={(e) => {
                              e.target.style.display = "none";
                              setLoadedImages((prev) => ({
                                ...prev,
                                [imageKey]: "fallback",
                              }));
                            }}
                          />
                          {loadedImages[imageKey] === "fallback" && (
                            <LogoFallback>
                              <StorefrontIcon
                                style={{ fontSize: 44, color: "#c3cfd4" }}
                              />
                            </LogoFallback>
                          )}
                          <MediaScrim aria-hidden="true" />
                          {/* Top-right. The open/closed pill and the share
                              button both left the photo on 11 Oct 2026 (Izi)
                              for the card body, so the photo carries only the
                              brand and the deal. */}
                          <VendorOfferTag
                            offer={signals.offerSummary}
                            corner="right"
                            radius={22}
                            size="m"
                            zIndex={2}
                          />
                          <IdentityRow>
                            {location.imageUrl && (
                              <LogoBadge>
                                <img
                                  src={getOptimizedUrl(
                                    location.imageUrl,
                                    "vendorLogo",
                                  )}
                                  alt=""
                                  loading="lazy"
                                />
                              </LogoBadge>
                            )}
                            <IdentityText>
                              <NameRow>
                                <RestaurantName>{name}</RestaurantName>
                              </NameRow>
                              {signals.cuisine && (
                                <CuisineChip title={signals.cuisine}>
                                  {signals.cuisine}
                                </CuisineChip>
                              )}
                            </IdentityText>
                          </IdentityRow>
                        </MediaFrame>
                      </CrawlLink>
                      <CardBody>
                        {(signals.isOpen != null || signals.orderAhead) && (
                          <StatePill
                            $open={!!signals.isOpen}
                            $orderAhead={signals.orderAhead}
                          >
                            {signals.orderAhead
                              ? ORDER_AHEAD_LABEL
                              : signals.isOpen
                                ? "Open now"
                                : "Closed"}
                          </StatePill>
                        )}
                        {/* After the pill in source order, so CardBody's
                            first-lines rule keeps it clear of the pill. */}
                        <RestaurantTagline vendor={location} />
                        {chainLabel && (
                          <>
                            {/* Which of the restaurant's locations this card
                                opens on, and how far it is. Changed from the
                                storefront's location picker. */}
                            <ChainLocation>
                              {location.isOutlet ? location.name : "Main branch"}
                              {location.distance
                                ? ` · ${location.distance} km`
                                : ""}
                            </ChainLocation>
                            <ChainCount>{chainLabel}</ChainCount>
                          </>
                        )}
                        {matched.length > 0 && (
                          <MatchLine>
                            On the menu:{" "}
                            <b>
                              {matched
                                .map((dish) => String(dish.name || "").trim())
                                .join(" · ")}
                            </b>
                          </MatchLine>
                        )}
                        {signals.fromPrice != null && (
                          <PriceLine>
                            <b>from {formatCurrency(signals.fromPrice)}</b>
                            {signals.dishCount > 0
                              ? ` · ${signals.dishCount} ${
                                  signals.dishCount === 1 ? "dish" : "dishes"
                                }`
                              : ""}
                          </PriceLine>
                        )}
                        <Address
                          title={
                            location.deliveryZoneRadius
                              ? `${location.address} · delivers within ${location.deliveryZoneRadius} km`
                              : location.address
                          }
                        >
                          <LocationOnIcon style={{ fontSize: 15 }} />
                          <span>{location.address}</span>
                        </Address>
                        <CardFooter>
                          <DeliveryBadge
                            vendor={location}
                            coverage={
                              coverageByVendor[String(vendorIdFor(location))]
                            }
                            size="small"
                          />
                          {/* Bottom right of the card (Izi, 11 Oct 2026),
                              off the photo with the state pill. */}
                          <ShareButton
                            type="button"
                            onClick={(e) => handleShare(e, location, signals)}
                            aria-label={`Share ${location.name}`}
                            aria-haspopup="dialog"
                            title="Share this restaurant"
                          >
                            <ShareIcon fontSize="inherit" />
                          </ShareButton>
                        </CardFooter>
                      </CardBody>
                    </RestaurantCard>
                  );
                })}
              </RestaurantsGrid>
            </>
          )}
        </ContentSection>
        {/* Every restaurant storefront, including the ones the cards above leave out. */}
        <VendorDirectoryLinks vertical="restaurant" title="All restaurants on Lusuku" />
      </PageContainer>
      <Footer />

      {/* One sheet for the whole grid rather than one per card: only ever one
          can be open, and mounting ~30 of them to keep 29 hidden is work the
          page does not need to do. */}
      <VendorShareModal
        open={!!shareTarget}
        onClose={() => setShareTarget(null)}
        url={shareTarget?.url}
        title={shareTarget?.name}
        description={shareTarget?.description}
        imageUrl={shareTarget?.imageUrl}
        imageFit={shareTarget?.imageFit}
        shareText={
          shareTarget
            ? `${shareTarget.name}. Browse the menu and order:`
            : undefined
        }
      />
    </PageWrapper>
  );
};

export default AllRestaurantsPage;

// ── Styled Components ──────────────────────────────────────────────────────
const PageWrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
`;
/* The room the cards sit in.

   Warm near-white instead of the old cold #f6f8f8, and lit from the top: the
   gradient carries the hero's light a little way down so the page has ONE
   implied light source rather than being evenly flooded. Even flooding is
   what makes a page read as a canteen. A warm ground reads as comfortable and
   holds attention; cool grey-green reads clinical, which is the last thing a
   list of restaurants should feel like. */
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #fbf7f1 0px, #faf8f5 420px, #faf8f5 100%);
  overflow-x: hidden;
  max-width: 100vw;
`;

/* The threshold.

   This was a flat pale mint band, the same luminance as everything below it,
   which meant the page had no light in it at all: nothing was emphasised,
   nothing receded, and the eye had nowhere to land. Restaurants have solved
   this for a century by doing the opposite of a bright even room. They dim the
   space and put a warm pool of light on the table.

   So: a dark warm field, lit from overhead, with the plates themselves
   supplying the texture and the search field left as the single brightest
   object in it. Three things follow. The eye goes to the search field because
   it is the brightest thing, not because we made it bigger. The dark-to-light
   step at the fold reads as walking in from the street, which is a real
   threshold rather than a section break. And the white cards below are now
   BRIGHTER than their surroundings, so they read as lit objects without a
   single change to the cards themselves.

   Colour discipline, learned the hard way: ONE hue family and ONE light
   source. An earlier version put a warm ORANGE radial in the corner of a
   green-grey base, and orange over green is olive, which is exactly the muddy
   band it had. Warmth comes only from the amber pool overhead; the shadows
   stay cool.

   Stacking order in this section, bottom to top:
     2  ::before          film grain and the overhead amber pool
     4  HeaderContent
   The pool is offset to the left rather than centred, because the headline is
   left-aligned now and a light source should fall on the thing it is lighting. */
const HeaderSection = styled.div`
  position: relative;
  overflow: hidden;
  background-color: #0c1e24;
  padding: 2rem 1rem 0;

  /* Sits below the fixed navbar. Declared after the shorthand above so it
     wins. */
  padding-top: calc(var(--lusuku-nav-h, 130px) + 2.4rem);
  padding-bottom: 2.2rem;
  box-sizing: border-box;

  /* Five percent of noise is what stops a dark field reading as flat paint and
     starts it reading as a photograph of a lit room. The alpha is baked into
     the SVG rather than applied with mix-blend-mode, which keeps it from
     tinting the layers underneath. */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background-image:
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E"),
      radial-gradient(
        84% 72% at 26% -14%,
        rgba(247, 189, 34, 0.24) 0%,
        rgba(247, 189, 34, 0.045) 46%,
        rgba(247, 189, 34, 0) 74%
      );
    background-size:
      160px 160px,
      auto;
  }

  /* Deliberately NO bottom fade. Two earlier versions tried one, at 40px and
     at 26px, and both landed as a pale fog band smeared across the photograph:
     lightening a dark photographic field towards white over a few dozen pixels
     is not what a light falling off looks like, it is what a dirty lens looks
     like. A hard edge is the honest answer here, and it is also the better
     one, because the whole section is built to read as a threshold. Dark room,
     doorway, bright room. Doorways have edges. */

  @media (max-width: 850px) {
    padding-top: calc(var(--lusuku-nav-h, 130px) + 1.25rem);
  }
`;

/* THE BOARD.
 *
 * A hero with no photograph in it, on a page about food, which sounds like a
 * retreat and is the opposite. The grid below already carries a photograph for
 * every restaurant, so anything pictorial up here was the same food twice.
 * What is here instead is the only thing on this page a competitor cannot
 * copy: the live state of the market, set as information rather than
 * decorated as a graphic.
 *
 * Every rule below earns its place by being a fact, and the type does the rest.
 */
const HeaderTop = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 3rem;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    gap: 1.6rem;
    align-items: stretch;
  }
`;

const Board = styled.div`
  margin-top: 2.6rem;
`;

const BoardLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(240, 233, 220, 0.62);
  transition: color 0.16s ease;
`;

const BoardCount = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
  color: #0c1e24;
  background: #3ddc97;
  border-radius: 999px;
  min-width: 20px;
  padding: 1px 6px;
  text-align: center;
`;

const BoardChevron = styled.span`
  display: inline-flex;
  font-size: 20px;
  line-height: 0;
  color: rgba(240, 233, 220, 0.55);
  transform: rotate(${(props) => (props.$open ? "180deg" : "0deg")});
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* The head is the disclosure control. It stays a rule with two labels on it
   rather than growing a button's chrome: the chevron and the hover state are
   enough to say it is pressable, and a filled button here would out-shout the
   search field, which has to remain the brightest thing in this section. */
const BoardHead = styled.button`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1.25rem;
  flex-wrap: wrap;
  width: 100%;
  margin: 0;
  padding: 4px 0 12px;
  border: none;
  border-bottom: 1px solid rgba(255, 240, 222, 0.18);
  background: transparent;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.16s ease;

  &:hover {
    border-bottom-color: rgba(255, 240, 222, 0.34);
  }

  &:hover ${BoardLabel} {
    color: rgba(240, 233, 220, 0.9);
  }

  &:focus-visible {
    outline: 2px solid #f7bd22;
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

/* Collapsing with the `hidden` attribute rather than an animated height.
 *
 * Two animated versions shipped before this one and both broke in ways that
 * were invisible in review. The grid trick, easing grid-template-rows from 1fr
 * to 0fr, never opened at all: an overflow-hidden grid ITEM has an automatic
 * minimum size of zero, so the fr track resolved against zero free space while
 * "1fr" sat in the stylesheet looking correct. Measuring the content and easing
 * max-height then failed the other way, with the right rule on the element and
 * the browser holding the previous used value.
 *
 * `hidden` is one attribute, and it takes the rows out of layout, out of the
 * accessibility tree and out of the tab order in one move, with no measuring,
 * no observer and no timing to get wrong. A disclosure that opens instantly is
 * not worse than one that eases; a disclosure that does not open is.
 */
const BoardPanel = styled.div`
  /* BoardRow bleeds 12px past the content column on each side so its hover
     state has room; this keeps the column itself aligned with everything
     above it. */
  margin: 0 -12px;
  padding: 0 12px;

  /* Explicit, because the UA rule behind the hidden attribute is display:none
     and any display declaration added here later would silently outrank it. */
  &[hidden] {
    display: none;
  }
`;

const BoardMeta = styled.div`
  font-size: 0.85rem;
  color: rgba(240, 233, 220, 0.45);
  font-variant-numeric: tabular-nums;
`;

/* A row is a button because it does what the card does: opens that menu. It is
   NOT styled like one. A board of seven buttons would be a control panel. */
const BoardRow = styled.button`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 2rem;
  align-items: center;
  /* Bleeds 12px past the content column on each side so the hover state has
     room to breathe without the text moving. */
  margin: 0 -12px;
  width: calc(100% + 24px);
  padding: 16px 12px;
  border: none;
  border-bottom: 1px solid rgba(255, 240, 222, 0.07);
  border-radius: 8px;
  background: transparent;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.16s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.045);
  }

  &:focus-visible {
    outline: 2px solid #f7bd22;
    outline-offset: -2px;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 0.55rem;
  }
`;

const OpenDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #3ddc97;
  box-shadow: 0 0 0 3px rgba(61, 220, 151, 0.22);
  display: inline-block;
  flex: none;
`;

const BoardKitchen = styled.div`
  min-width: 0;

  b {
    display: flex;
    align-items: center;
    gap: 11px;
    color: #fff7ea;
    font-size: 1.08rem;
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  span {
    display: block;
    /* Indented past the dot so the cuisine hangs under the NAME rather than
       under the status light. */
    margin: 3px 0 0 18px;
    color: rgba(240, 233, 220, 0.5);
    font-size: 0.86rem;
  }
`;

/* The appetite half of the row. A dish somebody could order in the next hour,
   named and priced, is a more useful invitation than a photograph of it. */
const BoardLead = styled.div`
  text-align: right;
  min-width: 0;

  b {
    display: block;
    color: rgba(240, 233, 220, 0.9);
    font-size: 0.98rem;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    margin-top: 3px;
    color: rgba(240, 233, 220, 0.4);
    font-size: 0.86rem;
    font-variant-numeric: tabular-nums;
  }

  em {
    font-style: normal;
    font-weight: 700;
    color: #f7bd22;
  }

  @media (max-width: 700px) {
    text-align: left;
    margin-left: 18px;
  }
`;

const BoardFoot = styled.div`
  padding: 18px 0 2px;
  color: rgba(240, 233, 220, 0.42);
  font-size: 0.86rem;

  b {
    color: rgba(240, 233, 220, 0.72);
    font-weight: 600;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.26em;
  color: rgba(247, 189, 34, 0.92);
  text-transform: uppercase;
`;

/* Left-aligned on a wide screen, because the photograph now holds the right
   half and centred text would collide with it. Back to centred below the
   breakpoint, where the photograph is the background and there is no other
   side for the text to be on. */
const HeaderContent = styled.div`
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  text-align: left;
  box-sizing: border-box;
  padding: 0 1.5rem;
  position: relative;
  /* Above the grain and the overhead pool on HeaderSection::before. */
  z-index: 4;

  @media (max-width: 640px) {
    padding: 0 1.1rem;
  }
`;

const Title = styled.h1`
  /* Warm white, never pure white: #fff on a warm dark field reads as a cold
     hole punched in it. */
  color: #fff7ea;
  font-family: "Poppins", sans-serif;
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.04;
  letter-spacing: -0.04em;
  margin: 1.1rem 0 0;

  /* The single accent on the page. The meal word is the only part of this
     sentence that changes through the day and the only part that names
     anything, so it is the only part that gets colour. */
  em {
    font-style: normal;
    color: #f7bd22;
  }

  @media (max-width: 900px) {
    font-size: 2.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.85rem;
  }
`;

const SearchBar = styled.div`
  width: min(32rem, 100%);
  margin: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: none;
  border-radius: 14px;
  padding: 0 16px;
  /* The brightest object on the page, and it earns that by being lit rather
     than by being large: a deep cast shadow beneath it and a one-pixel
     highlight along its top edge, which is what a real object under an
     overhead light looks like. */
  box-shadow:
    0 20px 42px -14px rgba(0, 0, 0, 0.55),
    0 1px 0 rgba(255, 255, 255, 0.7) inset;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:focus-within {
    transform: translateY(-1px);
    box-shadow:
      0 24px 48px -14px rgba(0, 0, 0, 0.6),
      0 0 0 3px rgba(247, 189, 34, 0.35),
      0 1px 0 rgba(255, 255, 255, 0.7) inset;
  }

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 13px 0;
  border: none;
  font-size: 1rem;
  outline: none;
  font-family: "Inter", sans-serif;
  background: transparent;
  box-sizing: border-box;
  min-width: 0;

  &::placeholder {
    color: #9aa7ac;
  }
`;

/* Only rendered when there is something to clear, so it never sits there as a
   dead control. Round, quiet, and inside the field: a person reaches for the
   right-hand end of a search box by habit, and anything louder would compete
   with the field itself, which is the brightest object in this section. */
const ClearSearch = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-left: 4px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #eef1f2;
  color: #63757b;
  font-size: 16px;
  line-height: 0;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: #e0e5e7;
    color: #17252a;
  }

  &:focus-visible {
    outline: 2px solid #f7bd22;
    outline-offset: 2px;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 1.5rem;
  flex-wrap: wrap;
`;

const DeliveryFilterButton = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
  /* The inactive colour was #4ef81b, a neon green on white, which is close to
     unreadable in any light and was invisible against the old pale header.
     Amber on a translucent dark pill sits properly on the new field. */
  border: 1px solid
    ${(props) => (props.$active ? "#f7bd22" : "rgba(255, 240, 222, 0.35)")};
  background: ${(props) =>
    props.$active ? "#f7bd22" : "rgba(255, 255, 255, 0.08)"};
  color: ${(props) => (props.$active ? "#16211f" : "#ffeccb")};

  &:hover {
    border-color: #f7bd22;
    background: ${(props) =>
      props.$active ? "#ffcf4d" : "rgba(247, 189, 34, 0.18)"};
    color: ${(props) => (props.$active ? "#16211f" : "#fff3d6")};
  }
`;

/* 1400, not 1200. The page was leaving roughly 380px of a 1580px screen
   unused while simultaneously squeezing four cards into the middle, which is
   the worst of both: crowded content inside empty margins. Wider container,
   fewer columns, larger cards.

   The radial is the room's own light. It continues the hero's fall-off a
   little way into the grid so the first row sits in the brighter part of the
   page, which is where the eye already is. */
const ContentSection = styled.div`
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1.25rem 4rem;

  &::before {
    content: "";
    position: absolute;
    top: -40px;
    left: 8%;
    right: 8%;
    height: 380px;
    pointer-events: none;
    background: radial-gradient(
      60% 100% at 50% 0%,
      rgba(247, 189, 34, 0.09) 0%,
      rgba(247, 189, 34, 0) 70%
    );
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 1rem;
`;

const EmptyStateIconWrap = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #eef2f3;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
`;

const EmptyStateTitle = styled.h2`
  color: #17252a;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.4rem;
  font-family: "Poppins", sans-serif;
`;

const EmptyStateText = styled.p`
  color: #6b7a80;
  font-size: 0.95rem;
`;

const EmptyStateAction = styled.button`
  margin-top: 1.1rem;
  padding: 9px 18px;
  border-radius: 10px;
  border: 1px solid #03660c;
  background: #ffffff;
  color: #03660c;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: #03660c;
    color: #ffffff;
  }
`;

const ResultsCount = styled.p`
  color: #6b7a80;
  font-size: 0.9rem;
  margin-bottom: 1.1rem;
  font-weight: 500;
`;

const RestaurantsGrid = styled.div`
  display: grid;
  /* 360 rather than 270. At 1400 that settles on three columns of about
     448px, which is the width at which a plate of food stops being a
     thumbnail and starts being a photograph. Seven restaurants do not need to
     fit above the fold; each one needs to be worth looking at. */
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 1.75rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

/* 22px, and every curve inside the card now agrees with it: the media frame
   inherits it through overflow, the logo badge is fully round, the chips are
   pills. The old card was soft on the outside and hard everywhere inside,
   which is what made it read as a stack of rectangles in a rounded box.
   Rounded contours are read as approachable and safe; angular ones read as
   clinical, and that difference is not decoration on a page whose job is to
   make someone comfortable enough to order dinner. */
const RestaurantCard = styled.div`
  background: #ffffff;
  border-radius: 22px;
  cursor: pointer;
  transition:
    box-shadow 0.16s ease,
    transform 0.16s ease,
    border-color 0.16s ease;
  /* Two shadows, because one never looks like a real object. The tight dark
     one is contact: it says the card is TOUCHING the page. The wide soft one
     is ambient occlusion. Both are warm-tinted rather than neutral black,
     because a neutral shadow reads as print on paper and a warm one reads as
     something sitting under a lamp. Losing the border matters too: an outline
     is a drawn edge, and drawn edges are the opposite of lit. */
  box-shadow:
    0 1px 2px rgba(61, 42, 26, 0.1),
    0 10px 26px -10px rgba(61, 42, 26, 0.22);
  border: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 2px 4px rgba(61, 42, 26, 0.12),
      0 26px 52px -16px rgba(61, 42, 26, 0.34);
  }

  /* The photo comes toward you on hover. Lifting the card says "this is a
     thing"; moving the light and the subject says "look at this thing". */
  &:hover img[data-card-media] {
    transform: scale(1.045);
  }

  /* Touch screens have no hover, so the resting shadow carries the separation
     on its own. */
  @media (max-width: 760px) {
    box-shadow:
      0 1px 2px rgba(61, 42, 26, 0.12),
      0 12px 28px -12px rgba(61, 42, 26, 0.26);
  }
`;

/* The media frame, and the biggest decision on this page: the FOOD is the
   subject and the brand is the label on it.

   The card used to lead with the logo on a plate and give the dishes a 62px
   band split three ways, so each dish arrived as a 90x62 sliver of a square
   crop. Cramped and cut out, and correctly identified as such. A logo tells
   you who someone is; a photograph of their food tells you what you want,
   and only one of those two makes anybody order dinner.

   `aspect-ratio` rather than a fixed height, and 5/3 because that is exactly
   the shape the `cardHero` Cloudinary preset delivers. Matching them means
   g_auto performs the single crop and `cover` has nothing left to take, at
   every column width, which is what stops the "cut out" feeling returning
   the moment the grid reflows. */
const MediaFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 5 / 3;
  overflow: hidden;
  background: #efece7;
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition:
    transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1),
    filter 0.3s ease,
    opacity 0.4s ease;
  opacity: ${(props) => (props.$isLoaded === true ? 1 : 0)};

  /* Light doing information work. A closed kitchen is not a card with a
     label on it, it is a table whose lamp is off: less light, less colour.
     The state is legible before a single word is read, and the restaurant is
     still perfectly visible, which is the point, because closed blocks
     ordering and never blocks looking. */
  ${(props) =>
    props.$closed &&
    `
    filter: saturate(0.5) brightness(0.78);
  `}
`;

/* Reads the photo from the bottom up so the name, the badge and the cuisine
   sit on ground dark enough to carry white text, while the top two thirds of
   the food stay untouched. */
const MediaScrim = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to top,
    rgba(12, 20, 18, 0.82) 0%,
    rgba(12, 20, 18, 0.2) 40%,
    rgba(12, 20, 18, 0) 66%
  );
`;

/* Open / Order ahead / Closed. Top right of the card BODY (Izi, 11 Oct 2026),
   where it was the top left of the photo: the photo now carries only the
   brand and the deal, and the trading state sits with the facts it belongs
   to (branch, price, distance). On a white plate it is a flat tint with no
   shadow; the lamp dot stays, that is what says "on". Not a control: fully
   round and never navy, per the clickable-affordance rule. */
const StatePill = styled.span`
  position: absolute;
  top: 14px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  background: ${(props) =>
    props.$open
      ? "rgba(23, 129, 94, 0.1)"
      : props.$orderAhead
        ? "rgba(29, 78, 216, 0.09)"
        : "rgba(178, 59, 59, 0.09)"};
  font-size: 0.74rem;
  font-weight: 700;
  line-height: 1.2;
  color: ${(props) =>
    props.$open ? "#17815e" : props.$orderAhead ? "#1d4ed8" : "#b23b3b"};
  white-space: nowrap;
  z-index: 1;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    /* A bare dot is a bullet. A dot with light coming off it is a lamp, and
       "open" is a lamp being on. */
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.$open
          ? "rgba(23, 129, 94, 0.18)"
          : props.$orderAhead
            ? "rgba(29, 78, 216, 0.16)"
            : "rgba(178, 59, 59, 0.16)"};
  }
`;

/* The identity block, sitting on the photo. This is what keeps the brand
   ownership the logo work was for: a restaurant's mark ON a photograph of
   their own food claims that food more strongly than the same mark alone on
   a white plate above it. */
const IdentityRow = styled.div`
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

/* Rim light. The white ring and the cast shadow are what make a flat circle
   read as a physical disc resting on the photograph rather than a hole
   punched through it. */
const LogoBadge = styled.div`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #ffffff;
  border: 3px solid #ffffff;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.42);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 80%;
    height: 80%;
    object-fit: contain;
  }

  @media (max-width: 480px) {
    width: 56px;
    height: 56px;
  }
`;

const IdentityText = styled.div`
  min-width: 0;
`;

/* On the photo now, so it is warm light on dark rather than a chip. A chip
   here would be a third rectangle competing with the badge and the pill. */
/* The restaurant's own tagline, first thing under the photo. Nothing renders
   when the restaurant has not written one. */
const RestaurantTagline = styled(VendorTagline)`
  margin: 0 0 8px;
  font-size: 0.86rem;
`;

const CuisineChip = styled.div`
  margin-top: 3px;
  min-width: 0;
  color: rgba(255, 238, 214, 0.92);
  font-size: 0.8rem;
  font-weight: 600;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* A price floor answers "can I afford to look?" before the question is
   consciously asked, and a dish count answers "is it worth my time?". Both
   are already in the payload. */
const PriceLine = styled.div`
  font-size: 0.9rem;
  color: #3f4d52;

  b {
    font-weight: 700;
    color: #17252a;
  }
`;

/* Why this card is in the results. A search for "pilau" surfaces kitchens
   whose NAME contains no such word, and without this line those results look
   arbitrary rather than helpful. Shown only while searching, and only when the
   menu is what matched. */
const MatchLine = styled.div`
  font-size: 0.85rem;
  color: #6b7a80;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  b {
    font-weight: 600;
    color: #17252a;
  }
`;

/* Share sits ON the logo frame rather than in the card body. The body is the
   restaurant's identity block (name, address, delivery state) and adding a
   control to it makes that block read as a toolbar. The frame has room to
   spare now that the logo is padded, and top-right is where people already
   look for this.

   `opacity` rather than `display` on hover so it is present for touch, where
   there is no hover at all, and so keyboard focus can reveal it. */
/* Share. Bottom right of the card body (Izi, 11 Oct 2026), beside the
   delivery badge, where it used to float on the photo. It is the one control
   on the card besides the card itself, so it is the one thing drawn as a
   button: a navy outline circle that fills on hover, per the clickable
   affordance rule. */
const ShareButton = styled.button`
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1.5px solid #083860;
  border-radius: 50%;
  background: #ffffff;
  color: #083860;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    transform 0.15s ease;

  svg {
    font-size: 17px;
  }

  &:hover {
    background: #083860;
    color: #ffffff;
    transform: scale(1.06);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(8, 56, 96, 0.28);
  }

  /* The confirmed/"copied" state used to live here, back when this button
     copied a link on the spot. Copying now happens inside the share sheet,
     which confirms in place next to the link it copied. */
`;

const CardBody = styled.div`
  position: relative;
  padding: 1.05rem 1.2rem 1.2rem;
  display: flex;
  flex-direction: column;
  flex: 1;

  /* The state pill sits absolute at the top right, so the first two text
     lines (branch and count, or the price line) stop short of it instead of
     running underneath. */
  > ${StatePill} ~ :nth-child(-n + 3) {
    padding-right: 112px;
  }
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const RestaurantName = styled.h3`
  color: #ffffff;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  font-size: 1.22rem;
  font-weight: 700;
  margin: 0;
  font-family: "Poppins", sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* A chain's card: the branch it opens on, then how many it has. Replaces
   the "Outlet" chip and the "Branch of" line, which only made sense while
   every branch was a card of its own (4 Oct 2026). */
const ChainLocation = styled.div`
  color: #3f4d52;
  font-size: 0.85rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChainCount = styled.div`
  color: #9aa7ac;
  font-size: 0.78rem;
  margin-bottom: 4px;
`;

/* Demoted. The address used to be the second thing on the card, competing
   with the name for a decision nobody makes on address. It is reference, so
   it reads as reference now. */
const Address = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #98a4a8;
  font-size: 0.76rem;
  margin-top: 0.5rem;

  svg {
    color: #9aa7ac;
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 0.55rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

/* ======================== */
/* Shimmer Skeleton Styles  */
/* ======================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const SkeletonPulse = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  border-radius: 8px;
`;

const SkeletonContainer = styled.div`
  animation: ${fadeIn} 0.3s ease;
`;

const SkeletonResultsCount = styled(SkeletonPulse)`
  width: 200px;
  height: 15px;
  margin-bottom: 1.1rem;
  border-radius: 6px;
`;

const SkeletonCard = styled.div`
  background: white;
  border-radius: 22px;
  border: 1px solid #eef1f2;
  box-shadow: 0 1px 4px rgba(20, 40, 45, 0.06);
  overflow: hidden;
`;

/* Mirrors MediaFrame. Same aspect rather than a copied pixel height, so the
   two cannot drift when the grid reflows. */
const SkeletonLogoArea = styled(SkeletonPulse)`
  width: 100%;
  aspect-ratio: 5 / 3;
  height: auto;
  border-radius: 0;
`;

const SkeletonCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0.9rem 1rem 1rem;
`;

const SkeletonLine = styled(SkeletonPulse)`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "14px"};
  border-radius: 6px;
`;

const SkeletonCircle = styled(SkeletonPulse)`
  width: ${(props) => props.size || "20px"};
  height: ${(props) => props.size || "20px"};
  min-width: ${(props) => props.size || "20px"};
  border-radius: 50%;
`;

const SkeletonAddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* ================================ */
/* Progressive Image Loading Styles */
/* ================================ */

const logoShimmer = keyframes`
  0% { background-position: -300px 0; }
  100% { background-position: 300px 0; }
`;

const LogoSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f5f5f5 25%, #ebebeb 37%, #f5f5f5 63%);
  background-size: 600px 100%;
  animation: ${logoShimmer} 1.4s ease-in-out infinite;
`;

/* Shown only when the hero image itself fails to load. Warm white so the
   gap in the row reads as a missing photograph rather than as a different
   card design. */
const LogoFallback = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
`;
